// Search + status/priority/type/assignee filters + group buttons +
// sort dropdown. State is held in the URL via react-router so deep
// links carry the filter context. The shell wires onChange via
// searchParams.set().
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router";
import {
  JOB_PRIORITIES,
  type GroupBy,
} from "../../../../../../shared/types/production-schedule";
import type {
  SolutionTypeDto,
  TeamMemberDto,
} from "../../../../../../shared/types/production-schedule";
import { useActiveJobStatuses } from "./JobStatusesProvider";

interface Props {
  team: TeamMemberDto[];
  types: SolutionTypeDto[];
  showGroup: boolean;
  searchPlaceholder?: string;
  // Optional control rendered to the left of the search input. Used
  // by the Board/List views to host the Jobs/Spools entity toggle.
  leading?: ReactNode;
}

const GROUPS: { id: GroupBy; label: string }[] = [
  { id: "status", label: "Status" },
  { id: "type", label: "Type" },
  { id: "assignee", label: "Assignee" },
  { id: "priority", label: "Priority" },
];

export function FilterBar({ team, types, showGroup, searchPlaceholder, leading }: Props) {
  const [params, setParams] = useSearchParams();
  const statuses = useActiveJobStatuses();
  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };
  const group = (params.get("group") as GroupBy) ?? "status";
  const sort = params.get("sort") ?? "priority";

  // Debounce the search input so each keystroke doesn't refetch.
  // Local state stays in sync with the URL when filters change from
  // elsewhere (back button, tab share); typing pushes after 280ms idle.
  const urlQ = params.get("q") ?? "";
  const [qDraft, setQDraft] = useState(urlQ);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => { setQDraft(urlQ); }, [urlQ]);
  const onQ = (v: string) => {
    setQDraft(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setParam("q", v), 280);
  };

  return (
    <>
      <div className="filter-row">
        {leading}
        <div className="search-box">
          <input
            type="text"
            placeholder={searchPlaceholder ?? "Search jobs…"}
            value={qDraft}
            onChange={(e) => onQ(e.target.value)}
          />
        </div>
        <select
          className="filter-select"
          value={params.get("status") ?? ""}
          onChange={(e) => setParam("status", e.target.value)}
        >
          {/* Empty value = the new 'All Active' default (hides
              jobs whose status is is_terminal). __all opts back into
              the legacy 'All Statuses' view; specific labels filter
              by exact match. */}
          <option value="">All Active</option>
          <option value="__all">All Statuses</option>
          {statuses.map((s) => (
            <option key={s.statusKey} value={s.label}>{s.label}</option>
          ))}
        </select>
        <select
          className="filter-select"
          value={params.get("priority") ?? ""}
          onChange={(e) => setParam("priority", e.target.value)}
        >
          <option value="">All Priorities</option>
          {JOB_PRIORITIES.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
        <select
          className="filter-select"
          value={params.get("solutionTypeId") ?? ""}
          onChange={(e) => setParam("solutionTypeId", e.target.value)}
        >
          <option value="">All Types</option>
          {types.map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
        <select
          className="filter-select"
          value={params.get("assigneeId") ?? ""}
          onChange={(e) => setParam("assigneeId", e.target.value)}
        >
          <option value="">All Assignees</option>
          {team.map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
        {showGroup && (
          <div className="group-btns">
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase", color: "var(--bsp-fg-muted)", marginRight: 4 }}>Group</span>
            {GROUPS.map((g) => (
              <button
                key={g.id}
                type="button"
                className={`group-btn ${g.id === group ? "active" : ""}`}
                onClick={() => setParam("group", g.id === "status" ? "" : g.id)}
              >
                {g.label}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="sort-row">
        <label>Sort</label>
        <select value={sort} onChange={(e) => setParam("sort", e.target.value === "priority" ? "" : e.target.value)}>
          <option value="priority">Priority</option>
          <option value="date">Due Date</option>
          <option value="startDate">Start Date</option>
          <option value="id">Job #</option>
          <option value="name">Name</option>
        </select>
      </div>
    </>
  );
}
